import type { Pt } from './dwgMeasure';

export type IdPointKind = 'survey' | 'block' | 'vertex';

// m maps an SVG point to world X/Y: X = m0·sx + m1·sy + m2, Y = m3·sx + m4·sy + m5.
export type IdFrame = {
  clip: [number, number, number, number];
  m: [number, number, number, number, number, number];
};

// tab-N.points.json written by the converter: points are [sx, sy, X, Y, Z, kind]
// with sx/sy already placed in that tab's SVG (through its viewport on a sheet).
export type IdPointsFile = {
  version: number;
  frames: IdFrame[];
  points: number[][];
  truncated?: boolean;
};

export type IdWorld = { x: number; y: number; z: number | null };

export type IdHit = { doc: Pt; world: IdWorld; kind: IdPointKind };

export type IdIndex = {
  frames: IdFrame[];
  sx: Float64Array;
  sy: Float64Array;
  wx: Float64Array;
  wy: Float64Array;
  wz: Float64Array;
  kind: Uint8Array;
  cells: Map<number, number[]>;
  minX: number;
  minY: number;
  cellW: number;
  cellH: number;
  truncated: boolean;
};

const KINDS: IdPointKind[] = ['survey', 'block', 'vertex'];
const GRID = 256;

const finite = (...v: number[]) => v.every(Number.isFinite);

export function buildIdIndex(file: IdPointsFile): IdIndex {
  const rows = (file.points || []).filter((p) => Array.isArray(p) && p.length >= 5 && finite(p[0], p[1], p[2], p[3], p[4]));
  const n = rows.length;
  const idx: IdIndex = {
    frames: (file.frames || []).filter((f) => f.clip?.length === 4 && f.m?.length === 6 && finite(...f.clip, ...f.m)),
    sx: new Float64Array(n), sy: new Float64Array(n),
    wx: new Float64Array(n), wy: new Float64Array(n), wz: new Float64Array(n),
    kind: new Uint8Array(n),
    cells: new Map(),
    minX: 0, minY: 0, cellW: 1, cellH: 1,
    truncated: !!file.truncated,
  };
  if (!n) return idx;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  rows.forEach((p, i) => {
    idx.sx[i] = p[0]; idx.sy[i] = p[1];
    idx.wx[i] = p[2]; idx.wy[i] = p[3]; idx.wz[i] = p[4];
    idx.kind[i] = Math.min(Math.max(Math.round(p[5] ?? 0), 0), KINDS.length - 1);
    minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
    minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]);
  });
  idx.minX = minX; idx.minY = minY;
  idx.cellW = Math.max((maxX - minX) / GRID, 1e-9);
  idx.cellH = Math.max((maxY - minY) / GRID, 1e-9);
  for (let i = 0; i < n; i++) {
    const key = cellKey(idx, idx.sx[i], idx.sy[i]);
    const list = idx.cells.get(key);
    if (list) list.push(i); else idx.cells.set(key, [i]);
  }
  return idx;
}

function cellCoord(idx: IdIndex, x: number, y: number): [number, number] {
  return [
    Math.min(GRID - 1, Math.max(0, Math.floor((x - idx.minX) / idx.cellW))),
    Math.min(GRID - 1, Math.max(0, Math.floor((y - idx.minY) / idx.cellH))),
  ];
}

function cellKey(idx: IdIndex, x: number, y: number): number {
  const [cx, cy] = cellCoord(idx, x, y);
  return cy * GRID + cx;
}

// Nearest snap target within `tol` (SVG units). Equal distances prefer the
// stronger kind — a surveyed point over a block insert over a vertex.
export function nearestIdPoint(idx: IdIndex, p: Pt, tol: number): IdHit | null {
  if (!idx.cells.size || !(tol > 0)) return null;
  const [x0, y0] = cellCoord(idx, p.x - tol, p.y - tol);
  const [x1, y1] = cellCoord(idx, p.x + tol, p.y + tol);
  let best = -1;
  let bestD = tol * tol;
  for (let cy = y0; cy <= y1; cy++) {
    for (let cx = x0; cx <= x1; cx++) {
      const list = idx.cells.get(cy * GRID + cx);
      if (!list) continue;
      for (const i of list) {
        const dx = idx.sx[i] - p.x, dy = idx.sy[i] - p.y;
        const d = dx * dx + dy * dy;
        if (d < bestD || (d === bestD && best >= 0 && idx.kind[i] < idx.kind[best])) {
          best = i;
          bestD = d;
        }
      }
    }
  }
  if (best < 0) return null;
  return {
    doc: { x: idx.sx[best], y: idx.sy[best] },
    world: { x: idx.wx[best], y: idx.wy[best], z: idx.wz[best] },
    kind: KINDS[idx.kind[best]],
  };
}

// World X/Y under an SVG point. Sheets can stack viewports; the last frame
// is the one drawn on top.
export function worldAt(frames: IdFrame[], p: Pt): { x: number; y: number } | null {
  for (let i = frames.length - 1; i >= 0; i--) {
    const { clip, m } = frames[i];
    if (p.x >= clip[0] && p.x <= clip[2] && p.y >= clip[1] && p.y <= clip[3]) {
      return { x: m[0] * p.x + m[1] * p.y + m[2], y: m[3] * p.x + m[4] * p.y + m[5] };
    }
  }
  return null;
}

// Model tabs converted before the sidecar existed still carry data-model-bbox:
// the model box fit margin-free onto the viewBox, y flipped.
export function bboxFrame(
  box: { minX: number; minY: number; maxX: number; maxY: number },
  vb: { x: number; y: number; w: number; h: number },
): IdFrame | null {
  if (!(box.maxX > box.minX && box.maxY > box.minY && vb.w > 0 && vb.h > 0)) return null;
  const a = (box.maxX - box.minX) / vb.w;
  const e = -(box.maxY - box.minY) / vb.h;
  return {
    clip: [vb.x, vb.y, vb.x + vb.w, vb.y + vb.h],
    m: [a, 0, box.minX - a * vb.x, 0, e, box.maxY - e * vb.y],
  };
}

export function formatIdCoord(v: number | null, decimals = 3): string {
  return v === null || !Number.isFinite(v) ? '—' : v.toFixed(decimals);
}

export function idPointText(world: IdWorld): string {
  return `X ${formatIdCoord(world.x)}   Y ${formatIdCoord(world.y)}   Z ${formatIdCoord(world.z)}`;
}

export function idPointClipboard(world: IdWorld): string {
  return [world.x, world.y, world.z].map((v) => formatIdCoord(v)).join(', ');
}
